import { useEffect } from 'preact/hooks';
import { defaultRelayUrl } from '../telemetry/RemoteTelemetryProvider';
import { relayKeyForRoom, forgetRoomKey } from './relayKey';
import { loadSettings, saveSettings } from './settings';
import { currentSession } from '../core/Session';
import { navigate } from '../ui/App';

/**
 * Remote control from the ROARDRIVE phone app: the app publishes into the relay
 * room ({type:'control', car, action}); the car browser follows — pick a car,
 * go home, switch to CAN. Always-on lightweight subscription (one WS).
 */
export function useRelayControl(): void {
  useEffect(() => {
    // the control channel is for CAR SCREENS (Tesla browser). Inside the phone app the page must not
    // listen: it would count itself as a screen AND navigate itself on a hand-off (Marc's phantom button)
    if (/ROARDRIVE-App/.test(navigator.userAgent)) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let room = loadSettings().relayRoom;
    let key = '';
    const open = () => {
      if (closed) return;
      const next = loadSettings().relayRoom;
      if (next !== room) { room = next; key = ''; }
      if (!key) {   // the relay wants the room's key before it opens the door
        void relayKeyForRoom(room).then((k) => { key = k; if (!closed) open(); }).catch(() => { if (!closed) setTimeout(open, 4000); });
        return;
      }
      try { ws = new WebSocket(`${defaultRelayUrl(room)}?role=sub&key=${encodeURIComponent(key)}`); } catch { return; }
      let opened = false;
      ws.onopen = () => { opened = true; };
      ws.onclose = () => { if (!opened) { forgetRoomKey(room); key = ''; } if (!closed) setTimeout(open, 3000); };
      ws.onmessage = (ev) => {
        let m: { type?: string; car?: string; action?: string; value?: string };
        try { m = JSON.parse(String(ev.data)); } catch { return; }
        if (m.type !== 'control') return;
        if (m.car) {
          // the phone app is the bridge → CAN is the source from now on
          if (loadSettings().source !== 'can') { saveSettings({ source: 'can' }); currentSession()?.setTelemetrySource('can'); }
          try { sessionStorage.setItem('roardrive:autostart', '1'); } catch { /* ignore */ }
          navigate(`/car/${m.car}`);
        } else if (m.action === 'home') {
          navigate('/');
        } else if (m.action === 'source' && (m.value === 'gps' || m.value === 'can' || m.value === 'sim')) {
          saveSettings({ source: m.value });
          currentSession()?.setTelemetrySource(m.value);
        }
      };
    };
    open();
    // room may change (account link / manual edit) → reconnect
    const iv = setInterval(() => { if (loadSettings().relayRoom !== room) { try { ws?.close(); } catch { /* ignore */ } } }, 2000);
    return () => { closed = true; clearInterval(iv); try { ws?.close(); } catch { /* ignore */ } };
  }, []);
}
